/** EDM panels — document register, transmittal register, vendor document register.
 *
 * Same dense 12-column idiom as the NFA module: Stat strip, Panel shell, sticky column
 * header, one grid row per record, Pill for state. Every number shown here is derived on
 * read by the API — the three notions of current, the ageing, the required-by dates — so
 * nothing on screen can drift from a stored flag.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, BellRing, Loader2, Plus, Search } from 'lucide-react';
import api from '@/lib/api';
import EdmBulkBar from '@/components/edm/EdmBulkBar';
import EdmTransmittalCreate from '@/components/edm/EdmTransmittalCreate';
import { useNfaTheme, day } from '@/components/nfa/nfaTheme';
import { Btn, Empty, Field, Input, Panel, Pill, Select, Stat } from '@/components/nfa/NfaPrimitives';

const HeadRow = ({ T, cols, testId }) => (
  <div className="grid grid-cols-12 px-4 py-2 text-[9.5px] uppercase tracking-[0.08em] sticky top-0 z-10"
    data-testid={testId}
    style={{ background: T.surfaceAlt, borderBottom: `1px solid ${T.border}`, color: T.muted }}>
    {cols.map(([label, span, align]) => (
      <div key={label} className={`col-span-${span} ${align || ''}`}>{label}</div>
    ))}
  </div>
);

const RevCell = ({ T, rev, tone }) => {
  if (!rev) return <span className="text-[10px]" style={{ color: T.muted }}>none yet</span>;
  return (
    <span className="inline-flex flex-col">
      <span className="font-mono text-[11px] tabular-nums" style={{ color: tone }}>
        {rev.revision_code}{rev.status_code ? ` · ${rev.status_code}` : ''}
      </span>
      <span className="text-[9.5px]" style={{ color: T.muted }}>
        {rev.issue_date ? day(rev.issue_date) : 'not issued'}
      </span>
    </span>
  );
};

/* ── Documents ─────────────────────────────────────────────────────────── */
export const EdmDocumentsPanel = ({ projectId }) => {
  const T = useNfaTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [discipline, setDiscipline] = useState('');
  const [meta, setMeta] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ document_no: '', title: '', discipline: 'Piping',
    doc_type: 'Drawing', area: '', system: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [allMatching, setAllMatching] = useState(false);

  const load = useCallback(() => {
    if (!projectId) return;
    const qs = new URLSearchParams();
    if (q) qs.set('q', q);
    if (discipline) qs.set('discipline', discipline);
    api.get(`/edm/projects/${projectId}/documents?${qs}`).then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Could not load the register'));
  }, [projectId, q, discipline]);
  useEffect(() => { const h = setTimeout(load, q ? 300 : 0); return () => clearTimeout(h); },
    [load, q]);
  useEffect(() => {
    if (projectId) api.get(`/edm/meta?project_id=${projectId}`).then((r) => setMeta(r.data))
      .catch(() => {});
  }, [projectId]);

  const disciplines = useMemo(
    () => [...new Set((data?.documents || []).map((d) => d.discipline).filter(Boolean))].sort(),
    [data]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`/edm/projects/${projectId}/documents`, form);
      navigate(`/engineering/documents/${r.data.document_id}`);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Could not register the document');
    } finally { setBusy(false); }
  };

  const docs = data?.documents || [];
  // Selection lives here, next to the filter it is resolved against: "select all matching
  // the filter" has to mean the same thing to the user and to the server.
  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  return (
    <div className="space-y-3" data-testid="edm-documents-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat T={T} label="Documents" value={data?.count ?? '—'} testId="edm-stat-documents"
          info="Registered documents on this project. A document is the identity; revisions
                and files hang off it." />
        <Stat T={T} label="Notions disagree" value={data?.documentsWhereNotionsDisagree ?? '—'}
          tone={data?.documentsWhereNotionsDisagree ? T.amber : undefined}
          sub="latest · latest approved · for construction"
          testId="edm-stat-disagree"
          info="Documents where 'latest', 'latest approved' and 'current for construction'
                point at THREE different revisions. Normal on a live register, and the whole
                reason no single 'current revision' column is shown anywhere." />
        <Stat T={T} label="Disciplines" value={disciplines.length || '—'}
          testId="edm-stat-disciplines"
          info="Distinct disciplines present in the filtered register." />
        <Stat T={T} label="Revision scheme" value={meta?.revisionScheme?.key || '—'}
          sub={meta?.revisionScheme?.label} testId="edm-stat-scheme"
          info="The revision series this project issues in. It is paired with the status-code
                set; changing it never rewrites revisions already issued." />
      </div>

      <EdmBulkBar projectId={projectId} selected={selected} allMatching={allMatching}
        setAllMatching={setAllMatching} matchingCount={docs.length}
        filters={{ q: q || undefined, discipline: discipline || undefined }}
        clear={() => setSelected(new Set())}
        onDone={() => load()} />

      <Panel T={T} testId="edm-documents-register" pad={false}
        title="Document register"
        subtitle={data?.note || 'Document → revision → version → file. No is_current flag anywhere.'}
        right={<>
          <div className="relative">
            <Search className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2"
              style={{ color: T.muted }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="number or title"
              data-testid="edm-doc-search"
              className="pl-7 pr-2 py-1.5 rounded-md text-[11px] outline-none w-44"
              style={{ background: T.page, border: `1px solid ${T.border}`, color: T.ink }} />
          </div>
          <Select T={T} value={discipline} onChange={(e) => setDiscipline(e.target.value)}
            data-testid="edm-doc-discipline">
            <option value="">All disciplines</option>
            {disciplines.map((d) => <option key={d} value={d}>{d}</option>)}
          </Select>
          <label className="inline-flex items-center gap-1.5 text-[11px]"
            style={{ color: T.muted }} data-testid="edm-select-all-label">
            <input type="checkbox" data-testid="edm-select-all"
              checked={docs.length > 0 && selected.size === docs.length}
              onChange={(e) => { setAllMatching(false);
                setSelected(e.target.checked ? new Set(docs.map((d) => d.document_id)) : new Set()); }} />
            select {docs.length}
          </label>
          <Btn T={T} onClick={() => setCreating(true)} testId="edm-register-document-btn">
            <Plus className="w-3.5 h-3.5" /> Register document
          </Btn>
        </>}>
        <HeadRow T={T} testId="edm-doc-head" cols={[['Document no.', 2], ['Title', 3],
          ['Discipline · type', 2], ['Latest', 1], ['Latest approved', 2],
          ['For construction', 1], ['Revs', 1, 'text-right']]} />
        {!data && <Empty T={T} testId="edm-doc-loading">
          <Loader2 className="w-4 h-4 animate-spin inline" /> Loading register…</Empty>}
        {data && docs.length === 0 && <Empty T={T} testId="edm-doc-empty">
          No document matches. Clear the search, or register the first document.</Empty>}
        {docs.map((d) => (
          <div key={d.document_id} role="button" tabIndex={0}
            onClick={() => navigate(`/engineering/documents/${d.document_id}`)}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/engineering/documents/${d.document_id}`)}
            data-testid={`edm-doc-row-${d.document_no}`}
            className="w-full grid grid-cols-12 px-4 py-2.5 text-left text-[11.5px] items-center hover:opacity-90 cursor-pointer"
            style={{ borderBottom: `1px solid ${T.border}` }}>
            <div className="col-span-2 font-mono text-[10.5px] tabular-nums flex items-center gap-2"
              style={{ color: T.primary }}>
              <input type="checkbox" checked={selected.has(d.document_id)}
                data-testid={`edm-doc-select-${d.document_no}`}
                onClick={(e) => e.stopPropagation()}
                onChange={() => { setAllMatching(false); toggle(d.document_id); }} />
              {d.document_no}</div>
            <div className="col-span-3 truncate pr-2" style={{ color: T.ink }}>{d.title}</div>
            <div className="col-span-2 truncate pr-2" style={{ color: T.body }}>
              {[d.discipline, d.doc_type].filter(Boolean).join(' · ') || '—'}</div>
            <div className="col-span-1"><RevCell T={T} rev={d.latestRevision} tone={T.ink} /></div>
            <div className="col-span-2"><RevCell T={T} rev={d.latestApprovedRevision} tone={T.primary} /></div>
            <div className="col-span-1"><RevCell T={T} rev={d.currentForConstruction} tone={T.green} /></div>
            <div className="col-span-1 text-right tabular-nums" style={{ color: T.muted }}>
              {d.revisionCount}
              {d.distinctNotions > 1 && (
                <span className="ml-1.5 px-1 rounded text-[9px]"
                  data-testid={`edm-doc-disagree-${d.document_no}`}
                  style={{ background: T.tint(T.amber), color: T.amber }}>
                  {d.distinctNotions}≠</span>)}
            </div>
          </div>
        ))}
      </Panel>

      {creating && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-6"
          style={{ background: '#000000CC' }} data-testid="edm-register-dialog">
          <form onSubmit={create} className="w-full max-w-2xl rounded-lg p-5"
            style={{ background: T.surface, border: `1px solid ${T.border}` }}>
            <h3 className="text-[15px] font-semibold" style={{ color: T.ink,
              fontFamily: '"Space Grotesk", Inter, sans-serif' }}>Register a document</h3>
            <p className="text-[10.5px] mt-1" style={{ color: T.muted }}>
              Number and title only. Revisions and files are opened against it afterwards — a
              document with no revision yet is a normal state, not a missing record.
            </p>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <Field T={T} label="Document number">
                <Input T={T} required value={form.document_no} data-testid="edm-field-document-no"
                  placeholder="AD-PIP-DWG-1203"
                  onChange={(e) => setForm({ ...form, document_no: e.target.value })} /></Field>
              <Field T={T} label="Title">
                <Input T={T} required value={form.title} data-testid="edm-field-title"
                  placeholder="Flare header line isometric"
                  onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
              <Field T={T} label="Discipline">
                <Select T={T} value={form.discipline} data-testid="edm-field-discipline"
                  onChange={(e) => setForm({ ...form, discipline: e.target.value })}>
                  {(meta?.disciplines || []).map((d) => <option key={d}>{d}</option>)}
                </Select></Field>
              <Field T={T} label="Document type">
                <Select T={T} value={form.doc_type} data-testid="edm-field-doc-type"
                  onChange={(e) => setForm({ ...form, doc_type: e.target.value })}>
                  {(meta?.docTypes || []).map((d) => <option key={d}>{d}</option>)}
                </Select></Field>
              <Field T={T} label="Area"><Input T={T} value={form.area} data-testid="edm-field-area"
                onChange={(e) => setForm({ ...form, area: e.target.value })} /></Field>
              <Field T={T} label="System"><Input T={T} value={form.system} data-testid="edm-field-system"
                onChange={(e) => setForm({ ...form, system: e.target.value })} /></Field>
            </div>
            {err && <p className="text-[11px] mt-3" style={{ color: T.red }}
              data-testid="edm-register-dialog-error">{err}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <Btn T={T} variant="ghost" onClick={() => setCreating(false)}>Cancel</Btn>
              <Btn T={T} type="submit" disabled={busy} testId="edm-register-dialog-submit">
                {busy && <Loader2 className="w-3 h-3 animate-spin" />} Register document</Btn>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

/* ── Transmittals ──────────────────────────────────────────────────────── */
export const EdmTransmittalsPanel = ({ projectId }) => {
  const T = useNfaTheme();
  const [data, setData] = useState(null);
  const [chase, setChase] = useState(null);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState('');
  const [issuing, setIssuing] = useState(false);

  const load = useCallback(() => {
    if (!projectId) return;
    const qs = state ? `?state=${state}` : '';
    api.get(`/edm/projects/${projectId}/transmittals${qs}`).then((r) => setData(r.data))
      .catch(() => {});
  }, [projectId, state]);
  useEffect(load, [load]);

  const run = async (dry) => {
    setBusy(true);
    try {
      const r = await api.post(`/edm/projects/${projectId}/transmittals/chase`,
        { dry_run: dry });
      setChase({ ...r.data, dry });
      if (!dry) load();
    } finally { setBusy(false); }
  };

  const rows = data?.transmittals || [];
  return (
    <div className="space-y-3" data-testid="edm-transmittals-panel">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat T={T} label="Transmittals" value={data?.count ?? '—'}
          testId="edm-stat-transmittals"
          info="Every transmittal on this project, inbound and outbound." />
        <Stat T={T} label="Unacknowledged" value={data?.outstanding ?? '—'}
          tone={data?.outstanding ? T.amber : undefined}
          sub="holding construction behind" testId="edm-stat-outstanding"
          info="Delivered but neither acknowledged nor deemed received. Until one of those
                happens the revision is NOT current for construction, which is the single
                most common cause of a site team building to a superseded sheet." />
        <Stat T={T} label="Awaiting delivery" value={data?.awaitingDelivery ?? '—'}
          sub="deeming clock not started" testId="edm-stat-awaiting"
          info="Issued but not yet delivered. The deemed-receipt period counts from DELIVERY,
                so nothing is ticking on these." />
        <Stat T={T} label="Delivery failed" value={data?.deliveryFailed ?? '—'}
          tone={data?.deliveryFailed ? T.red : undefined} sub="re-issue, do not chase"
          testId="edm-stat-delivery-failed"
          info="Delivery was attempted and failed. Chasing the recipient for something that
                never arrived wastes everybody's week — these need re-issuing." />
        <Stat T={T} label="Over 30 days" value={data?.ageing?.['over 30 days'] ?? 0}
          tone={data?.ageing?.['over 30 days'] ? T.red : undefined}
          testId="edm-stat-ageing-30"
          info="Outstanding transmittals aged over 30 days from delivery. Ageing is derived
                on read from the delivery date — nothing is stored." />
      </div>

      {issuing && (
        <EdmTransmittalCreate projectId={projectId}
          onIssued={() => { setIssuing(false); load(); }}
          onCancel={() => setIssuing(false)} />
      )}

      {chase && (
        <div className="rounded-lg px-3 py-2.5 text-[11px]" data-testid="edm-chase-result"
          style={{ background: T.tint(T.primary), border: `1px solid ${T.tint(T.primary, '44')}`,
            color: T.ink }}>
          {chase.dry ? 'Dry run — nothing sent. ' : 'Chase run. '}
          {chase.remindersSent} reminder(s), {chase.escalations} internal escalation(s),
          {' '}{chase.skipped} skipped. <span style={{ color: T.muted }}>{chase.note}</span>
        </div>
      )}

      <Panel T={T} testId="edm-transmittal-register" pad={false}
        title="Transmittal register"
        subtitle={data?.note || 'Acknowledgement is what moves a revision to current for construction.'}
        right={<>
          <Select T={T} value={state} onChange={(e) => setState(e.target.value)}
            data-testid="edm-transmittal-state">
            <option value="">All</option>
            <option value="outstanding">Outstanding</option>
            <option value="acknowledged">Acknowledged</option>
          </Select>
          <Btn T={T} onClick={() => setIssuing((v) => !v)} testId="edm-transmittal-new">
            <Plus className="w-3.5 h-3.5" /> {issuing ? 'Close' : 'Issue transmittal'}
          </Btn>
          <Btn T={T} variant="ghost" onClick={() => run(true)} disabled={busy}
            testId="edm-run-chase-dry">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}
            Preview chase
          </Btn>
          <Btn T={T} onClick={() => run(false)} disabled={busy} testId="edm-run-chase">
            Run chase
          </Btn>
        </>}>
        <HeadRow T={T} cols={[['Transmittal', 2], ['To', 2], ['Delivery', 2],
          ['Acknowledgement', 2], ['Outstanding', 1], ['Chased', 1], ['Next action', 2]]} />
        {!data && <Empty T={T}><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</Empty>}
        {data && rows.length === 0 && <Empty T={T} testId="edm-transmittals-empty">
          Nothing issued yet. A transmittal is what moves a revision to current for
          construction, so this register stays empty until one is issued.</Empty>}
        {rows.map((r) => {
          const tone = { escalate_internally: T.red, fix_delivery: T.red, remind: T.amber,
            confirm_delivery: T.amber }[r.nextAction?.action] || T.muted;
          return (
            <div key={r.transmittal_id} data-testid={`edm-transmittal-row-${r.number}`}
              className="grid grid-cols-12 px-4 py-2.5 text-[11.5px] items-start"
              style={{ borderBottom: `1px solid ${T.border}` }}>
              <div className="col-span-2 font-mono text-[10.5px]" style={{ color: T.primary }}>
                {r.number}
                <div style={{ color: T.muted }}>{r.itemCount} doc(s)</div></div>
              <div className="col-span-2 truncate pr-2" style={{ color: T.ink }}>{r.to_org}</div>
              <div className="col-span-2"><Pill T={T} value={r.delivery_state} />
                {r.delivered_on && <div className="text-[9.5px] mt-0.5" style={{ color: T.muted }}>
                  {day(r.delivered_on)}</div>}</div>
              <div className="col-span-2">
                <span className="text-[11px] font-semibold"
                  style={{ color: r.effectivelyAcknowledged ? T.green : T.amber }}>
                  {r.acknowledgementBasis || 'none'}</span>
                <div className="text-[9.5px] mt-0.5" style={{ color: T.muted }}>
                  {r.deemed?.reason}</div></div>
              <div className="col-span-1 tabular-nums" style={{ color: T.body }}>
                <div>{r.daysOutstanding ?? '—'}</div>
                {r.ageingBucket && <div className="text-[9.5px] whitespace-nowrap"
                  style={{ color: T.muted }}>{r.ageingBucket}</div>}</div>
              <div className="col-span-1 tabular-nums" style={{ color: T.body }}>
                {r.reminderCount}
                {r.reminderRefusals > 0 && <div className="text-[9.5px]" style={{ color: T.amber }}>
                  {r.reminderRefusals} refused</div>}</div>
              <div className="col-span-2">
                <span className="text-[10px] font-semibold uppercase tracking-wide"
                  style={{ color: tone }}>{(r.nextAction?.action || '').replace(/_/g, ' ')}</span>
                <div className="text-[9.5px] mt-0.5" style={{ color: T.muted }}>
                  {r.nextAction?.why}</div></div>
            </div>
          );
        })}
      </Panel>
    </div>
  );
};

/* ── Vendor document register ──────────────────────────────────────────── */
export const EdmVdrPanel = ({ projectId }) => {
  const T = useNfaTheme();
  const [data, setData] = useState(null);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ po_number: '', po_line: '', document_type_code: 'GA_DRAWING',
    title: '', milestone_name: '', milestone_date: '', planned_submission: '' });
  const [codes, setCodes] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!projectId) return;
    api.get(`/edm/projects/${projectId}/vdr${overdueOnly ? '?overdue_only=true' : ''}`)
      .then((r) => setData(r.data)).catch(() => {});
  }, [projectId, overdueOnly]);
  useEffect(load, [load]);
  useEffect(() => {
    if (projectId) api.get(`/edm/projects/${projectId}/vdr/review-codes`)
      .then((r) => setCodes(r.data.codes || [])).catch(() => {});
  }, [projectId]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.post(`/edm/projects/${projectId}/vdr`, form);
      setAdding(false); load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Could not create the obligation');
    } finally { setBusy(false); }
  };

  const rows = data?.rows || [];
  return (
    <div className="space-y-3" data-testid="edm-vdr-panel">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat T={T} label="Obligations" value={data?.count ?? '—'}
          testId="edm-stat-vdr-count"
          info="One row per document a vendor owes under a purchase order. The row exists
                from PO award — before any file does." />
        <Stat T={T} label="Nothing submitted" value={data?.notSubmitted ?? '—'}
          sub="normal at kick-off" testId="edm-stat-vdr-not-submitted"
          info="Obligations with no submission at all. Early in a package this is the
                expected state; late in one it is the schedule risk." />
        <Stat T={T} label="Overdue" value={data?.overdue ?? '—'}
          tone={data?.overdue ? T.red : undefined} testId="edm-stat-vdr-overdue"
          info="Past the required-by date, which is derived from the PO milestone and the
                review-cycle allowance for that document type — never typed in, so it moves
                when the milestone moves." />
        <Stat T={T} label="Awaiting review" value={data?.awaitingReview ?? '—'}
          testId="edm-stat-vdr-awaiting"
          info="Submitted by the vendor and sitting with the client. Every day here is a day
                of the client's own review allowance being spent." />
        <Stat T={T} label="Unverified PO refs" value={data?.unverifiedPoRefs ?? '—'}
          tone={data?.unverifiedPoRefs ? T.amber : undefined}
          sub="no matching PO line found" testId="edm-stat-vdr-unverified"
          info="The PO number on the row does not resolve to a PO line in procurement. The
                row is kept and flagged rather than rejected — the obligation is real even
                when the reference is wrong." />
      </div>

      <Panel T={T} testId="edm-vdr-register" pad={false}
        title="Vendor document register"
        subtitle={data?.note || 'One row per document a vendor owes under a purchase order.'}
        right={<>
          <Btn T={T} variant={overdueOnly ? 'warn' : 'ghost'}
            onClick={() => setOverdueOnly((v) => !v)} testId="edm-vdr-overdue-toggle">
            <AlertTriangle className="w-3.5 h-3.5" /> Overdue only
          </Btn>
          <Btn T={T} onClick={() => setAdding(true)} testId="edm-vdr-add">
            <Plus className="w-3.5 h-3.5" /> Add obligation
          </Btn>
        </>}>
        <HeadRow T={T} cols={[['PO · line', 2], ['Document type', 3], ['Vendor', 2],
          ['Required by', 2], ['State', 1], ['Code', 1], ['Status', 1]]} />
        {!data && <Empty T={T}><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</Empty>}
        {data && rows.length === 0 && <Empty T={T} testId="edm-vdr-empty">
          No obligations recorded. Import the PO&apos;s document requirements list, or add one
          by hand — rows exist before any file does.</Empty>}
        {rows.map((r) => (
          <div key={r.vdr_row_id} data-testid={`edm-vdr-row-${r.vdr_row_id}`}
            className="grid grid-cols-12 px-4 py-2.5 text-[11.5px] items-start"
            style={{ borderBottom: `1px solid ${T.border}` }}>
            <div className="col-span-2 font-mono text-[10.5px]" style={{ color: T.primary }}>
              {r.po_number}
              <div style={{ color: T.muted }}>line {r.po_line} · {r.po_source}
                {!r.po_resolved && <span style={{ color: T.amber }}> · unverified</span>}</div></div>
            <div className="col-span-3 pr-2" style={{ color: T.ink }}>
              {r.document_type_code}
              <div className="text-[9.5px]" style={{ color: T.muted }}>
                {r.title || '—'} · {r.scope} scope · basis {r.basis}</div></div>
            <div className="col-span-2 truncate pr-2" style={{ color: T.body }}>
              {r.vendor_name || '—'}
              {r.vendor_document_number && <div className="text-[9.5px] font-mono"
                style={{ color: T.muted }}>{r.vendor_document_number}</div>}</div>
            <div className="col-span-2 tabular-nums" title={r.requiredBy?.derivation}
              data-testid={`edm-vdr-required-${r.vdr_row_id}`}>
              <span style={{ color: T.ink }}>{r.requiredBy?.required_by || '—'}</span>
              <div className="text-[9.5px]" style={{ color: T.muted }}>{r.requiredBy?.basis}</div></div>
            <div className="col-span-1"><Pill T={T} value={r.state} /></div>
            <div className="col-span-1 font-mono text-[10.5px]" style={{ color: T.body }}>
              {r.review_code || '—'}
              {r.resubmission_count > 0 && <div className="text-[9.5px]" style={{ color: T.muted }}>
                {r.resubmission_count} resub</div>}</div>
            <div className="col-span-1 text-[9.5px]"
              style={{ color: r.overdue ? T.red : T.muted }}>{r.statement}</div>
          </div>
        ))}
      </Panel>

      {adding && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-6"
          style={{ background: '#000000CC' }} data-testid="edm-vdr-dialog">
          <form onSubmit={add} className="w-full max-w-2xl rounded-lg p-5"
            style={{ background: T.surface, border: `1px solid ${T.border}` }}>
            <h3 className="text-[15px] font-semibold" style={{ color: T.ink,
              fontFamily: '"Space Grotesk", Inter, sans-serif' }}>Add an obligation</h3>
            <p className="text-[10.5px] mt-1" style={{ color: T.muted }}>
              A VDR row is what a vendor owes under a PO. Link a milestone and the required-by
              date is derived, compounding over the expected resubmission cycles.
            </p>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <Field T={T} label="PO number"><Input T={T} required value={form.po_number}
                data-testid="edm-vdr-po" placeholder="PO-TEST-9001"
                onChange={(e) => setForm({ ...form, po_number: e.target.value })} /></Field>
              <Field T={T} label="PO line" hint="Left blank becomes line 00, never null.">
                <Input T={T} value={form.po_line} data-testid="edm-vdr-line"
                  onChange={(e) => setForm({ ...form, po_line: e.target.value })} /></Field>
              <Field T={T} label="Document type">
                <Select T={T} value={form.document_type_code} data-testid="edm-vdr-type"
                  onChange={(e) => setForm({ ...form, document_type_code: e.target.value })}>
                  {['GA_DRAWING', 'DATASHEET', 'OM_MANUAL', 'TEST_CERTIFICATE', 'NAMEPLATE']
                    .map((x) => <option key={x}>{x}</option>)}
                </Select></Field>
              <Field T={T} label="Title"><Input T={T} value={form.title} data-testid="edm-vdr-title"
                onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
              <Field T={T} label="Milestone name"><Input T={T} value={form.milestone_name}
                placeholder="FAT" data-testid="edm-vdr-milestone"
                onChange={(e) => setForm({ ...form, milestone_name: e.target.value })} /></Field>
              <Field T={T} label="Milestone date"><Input T={T} type="date"
                value={form.milestone_date} data-testid="edm-vdr-milestone-date"
                onChange={(e) => setForm({ ...form, milestone_date: e.target.value })} /></Field>
              <Field T={T} label="Planned submission"><Input T={T} type="date"
                value={form.planned_submission} data-testid="edm-vdr-planned"
                onChange={(e) => setForm({ ...form, planned_submission: e.target.value })} /></Field>
            </div>
            <p className="text-[10px] mt-3" style={{ color: T.muted }}>
              Review codes on this project: {codes.map((c) => `${c.code} ${c.label}`).join(' · ')}
            </p>
            {err && <p className="text-[11px] mt-2" style={{ color: T.red }}
              data-testid="edm-vdr-dialog-error">{err}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <Btn T={T} variant="ghost" onClick={() => setAdding(false)}>Cancel</Btn>
              <Btn T={T} type="submit" disabled={busy} testId="edm-vdr-dialog-submit">
                {busy && <Loader2 className="w-3 h-3 animate-spin" />} Add obligation</Btn>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
